import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Youtube, Instagram, Linkedin as LinkedIn, Mail, Phone, MapPin, Map } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer: React.FC = () => {
  return (
    <footer className="bg-gradient-to-br from-[#0a0a1f] via-[#14142b] to-[#1e1e2f] text-white border-t border-blue-400/10 backdrop-blur-xl font-[Inter,Poppins,sans-serif] antialiased overflow-hidden relative">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
          animate={{ 
            y: [0, -20, 0],
            x: [0, 10, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"
          animate={{ 
            y: [0, 20, 0],
            x: [0, -10, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 1
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-pink-500/10 rounded-full blur-3xl"
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.3, 1]
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity, 
            ease: "linear",
            delay: 2
          }}
        />
        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-purple-400 to-cyan-400 rounded-full opacity-30"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + i * 10}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.8,
            }}
          />
        ))}
      </div>

      <motion.div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {/* SEWA Services Section */}
          <motion.div 
            className="backdrop-blur-lg bg-white/5 border border-yellow-400/20 rounded-xl shadow-md p-4 hover:shadow-2xl hover:shadow-yellow-400/20 transition-all duration-300"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            viewport={{ once: true }}
          >
            <motion.h3 
              className="text-lg font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400 tracking-tight"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              SEWA Services
            </motion.h3>
            <ul className="space-y-2 text-blue-100 text-sm">
              {['Admission Support', 'College Selection', 'Document Check', 'Interview Prep', 'Career Guidance'].map((item, index) => (
                <li key={item}>
                  <motion.span
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-2xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400"
                  >
                    {item}
                  </motion.span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company Info */}
          <motion.div 
            className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-xl shadow-md p-5 hover:shadow-2xl hover:shadow-purple-400/20 transition-all duration-300"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.02, y: -5 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="flex items-center space-x-2 mb-4"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <motion.img 
                src="/SEWA-logo.webp" 
                alt="SEWA Logo" 
                className="w-10 h-10 rounded-lg object-cover shadow-lg border border-purple-500/30"
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                loading="lazy"
              />
              <span className="text-2xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400">SEWA</span>
            </motion.div>
            <motion.p 
              className="text-blue-100 mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Your trusted partner in educational journey. We provide comprehensive guidance for MBBS, Engineering, and scholarship opportunities.
            </motion.p>
            <div className="flex space-x-4">
              {[
                { Icon: Facebook, color: 'purple', title: 'Facebook', href: '#' },
                { Icon: Youtube, color: 'red', title: 'YouTube', href: '#' },
                { Icon: Instagram, color: 'pink', title: 'Instagram', href: 'https://www.instagram.com/namma_sewa?igsh=MWwxNnZmbm0wOWxmNQ==' },
                { Icon: LinkedIn, color: 'blue', title: 'LinkedIn', href: 'https://www.linkedin.com/in/info-sewa-026647379?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3B576Ub54NTcCB8fs3WrospA%3D%3D' }
              ].map(({ Icon, color, title, href }, index) => (
                <motion.a 
                  key={title}
                  href={href} 
                  className={`text-blue-100 hover:text-${color}-400 transition-colors`}
                  title={title}
                  target={href !== '#' ? '_blank' : undefined}
                  rel={href !== '#' ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.3, rotate: 10 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ 
                    duration: 0.3, 
                    delay: 0.5 + index * 0.1,
                    type: "spring",
                    stiffness: 300
                  }}
                  viewport={{ once: true }}
                >
                  <Icon className="h-6 w-6" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div 
            className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-xl shadow-md p-5 hover:shadow-2xl hover:shadow-cyan-400/20 transition-all duration-300"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.02, y: -5 }}
            viewport={{ once: true }}
          >
            <motion.h3 
              className="text-lg font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 tracking-tight"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Quick Links
            </motion.h3>
            <ul className="space-y-2">
              {[
                { to: "/about-us", text: "About Us", color: "cyan" },
                { to: "/mbbs", text: "MBBS Colleges", color: "purple" },
                { to: "/colleges", text: "Engineering Colleges", color: "cyan" },
                { to: "/arts-science", text: "Arts & Science", color: "pink" },
                { to: "/polytechnic", text: "Polytechnic", color: "cyan" },
                { to: "/abroad-studies", text: "Overseas Studies", color: "cyan" },
                { to: "/scholarships", text: "Scholarships", color: "pink" }

              ].map((link, index) => (
                <motion.li 
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link 
                    to={link.to} 
                    className={`text-blue-100 hover:text-${link.color}-400 transition-all duration-200 hover:pl-2 inline-block`}
                  >
                    <motion.span
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {link.text}
                    </motion.span>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Services section removed as requested */}

          {/* Contact Info */}
          <motion.div 
            className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-xl shadow-md p-5 hover:shadow-2xl hover:shadow-pink-400/20 transition-all duration-300"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ scale: 1.02, y: -5 }}
            viewport={{ once: true }}
          >
            <motion.h3 
              className="text-lg font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 tracking-tight"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Contact Us
            </motion.h3>
            <div className="space-y-3">
              {[
                { 
                  Icon: MapPin, 
                  text: "SEWA Head Office, Manickampalayam, Tiruchengode, Namakkal, Tamil Nadu , India", 
                  color: "purple",
                  animation: "animate-pulse",
                  isClickable: false
                },
                { 
                  Icon: Phone, 
                  text: "+91 77086 77132", 
                  color: "cyan",
                  animation: "animate-bounce",
                  isClickable: false
                },
                { 
                  Icon: Mail, 
                  text: "infonammasewa@gmail.com", 
                  color: "pink",
                  animation: "animate-pulse",
                  isClickable: false
                },
                { 
                  Icon: Map, 
                  text: "📍 View Location on Google Maps", 
                  color: "green",
                  animation: "animate-pulse",
                  isClickable: true,
                  href: "https://maps.app.goo.gl/Q6sWK4VNhn6ch2uv9"
                }
              ].map((contact, index) => (
                <motion.div 
                  key={contact.text}
                  className="flex items-center space-x-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  whileHover={{ x: 5 }}
                  viewport={{ once: true }}
                >
                  <contact.Icon className={`h-6 w-6 text-${contact.color}-400 ${contact.animation}`} />
                  {contact.isClickable ? (
                    <a 
                      href={contact.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-100 hover:text-green-400 transition-colors cursor-pointer"
                    >
                      {contact.text}
                    </a>
                  ) : (
                    <span className="text-blue-100 hover:text-white transition-colors cursor-pointer">{contact.text}</span>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        <motion.div 
          className="border-t border-blue-400/10 mt-8 pt-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.p 
            className="text-blue-400 text-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            © 2001{' '}
            <motion.span 
              className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 font-extrabold"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              SEWA
              <div className="text-xs text-blue-300 mt-1 font-medium">20+ Branches across Tamil Nadu</div>
            </motion.span>
            . All rights reserved. Made with{' '}
            <motion.span 
              className="text-pink-400 inline-block"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
            
            </motion.span>
            {' '}for students.
          </motion.p>
        </motion.div>
      </motion.div>
    </footer>
  );
};

export default Footer;